/**
 * Wire-format types for user-feedback records served by lexserver.
 *
 * Mirrors `lexserver/src/db/models/user-feedback.ts`. Dates arrive as
 * ISO strings (JSON round-trip). Keep these enums in lockstep with the
 * model file — they're the contract.
 */

export type FeedbackPolarity = "positive" | "negative" | "neutral";

export type FeedbackSurface =
  | "chat.message"
  | "chat.session"
  | "prompt.global"
  | "prompt.case"
  | "editor.document"
  | "editor.timeline"
  | "editor.deadlines"
  | "editor.parties"
  | "editor.petition"
  | "workspace.activity"
  | "workspace.breadcrumbs"
  | "sidepanel.tree"
  | "home.widget.deadlines"
  | "home.widget.cases"
  | "general";

export type FeedbackStatus = "open" | "triaged" | "resolved" | "wont_fix";

export type FeedbackEvaluationCategory =
  | "bug"
  | "feature_request"
  | "ai_accuracy"
  | "ai_helpfulness"
  | "ux_confusion"
  | "praise"
  | "off_topic";

export type FeedbackStatsGroupBy = "surface" | "polarity" | "category" | "orgId";

export interface FeedbackSurfaceContext {
  sessionKey?: string;
  runId?: string;
  caseId?: string;
  documentId?: string;
  petitionId?: string;
  deadlineId?: string;
  timelineEventId?: string;
  partyId?: string;
  model?: string;
  messageRole?: "user" | "assistant" | "system";
  messageExcerpt?: string;
  promptHash?: string;
  extra?: Record<string, unknown>;
}

export interface FeedbackClientMeta {
  appVersion: string;
  userAgent: string;
  locale: string;
  viewport?: { width: number; height: number };
  timestamp: string;
}

export interface FeedbackAiEvaluation {
  category: FeedbackEvaluationCategory;
  sentiment: "positive" | "neutral" | "negative" | "mixed";
  actionable: boolean;
  keyThemes: string[];
  summary: string;
  evaluatedAt: string;
  evaluatorVersion: string;
  model: string;
}

export interface FeedbackRecord {
  feedbackId: string;
  orgId: string;
  userId: string;
  polarity: FeedbackPolarity;
  comment?: string;
  surface: FeedbackSurface;
  surfaceContext: FeedbackSurfaceContext;
  clientMeta: FeedbackClientMeta;
  aiEvaluation?: FeedbackAiEvaluation;
  status: FeedbackStatus;
  triageNotes?: string;
  triagedBy?: string;
  triagedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FeedbackListParams {
  limit?: number;
  cursor?: string;
  orgId?: string;
  surface?: FeedbackSurface;
  polarity?: FeedbackPolarity;
  status?: FeedbackStatus;
  category?: FeedbackEvaluationCategory;
  q?: string;
  fromDate?: string;
  toDate?: string;
}

export interface FeedbackStatsParams {
  groupBy: FeedbackStatsGroupBy;
  orgId?: string;
  fromDate?: string;
  toDate?: string;
}

export interface FeedbackStatsResponse {
  groupBy: FeedbackStatsGroupBy;
  buckets: Array<{ key: string; count: number }>;
}

export const FEEDBACK_SURFACES: FeedbackSurface[] = [
  "chat.message",
  "chat.session",
  "prompt.global",
  "prompt.case",
  "editor.document",
  "editor.timeline",
  "editor.deadlines",
  "editor.parties",
  "editor.petition",
  "workspace.activity",
  "workspace.breadcrumbs",
  "sidepanel.tree",
  "home.widget.deadlines",
  "home.widget.cases",
  "general",
];

export const FEEDBACK_STATUSES: FeedbackStatus[] = [
  "open",
  "triaged",
  "resolved",
  "wont_fix",
];

export const FEEDBACK_CATEGORIES: FeedbackEvaluationCategory[] = [
  "bug",
  "feature_request",
  "ai_accuracy",
  "ai_helpfulness",
  "ux_confusion",
  "praise",
  "off_topic",
];
